/**
 * TawkHelper - Utility for interacting with Tawk.to chat.
 *
 * The support widget is available immediately in production. Optional
 * analytics identity is attached only for a consented public-demo session.
 */

import { telemetryClient } from '../features/telemetry/telemetryClient';

class TawkHelper {
  static loadPromise = null;

  static pendingDemoSessionId = null;

  static linkedDemoSessionId = null;

  static initialize() {
    if (typeof window === 'undefined' || window.APP_ENV !== 'production') {
      return Promise.resolve(false);
    }
    if (this.isAvailable()) return Promise.resolve(true);
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise((resolve) => {
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();
      const previousOnLoad = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = () => {
        if (typeof previousOnLoad === 'function') previousOnLoad();
        this.applyPendingIdentity();
        resolve(this.isAvailable());
      };

      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://embed.tawk.to/68cd46b9a9e99a19221babea/1j5gtohos';
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');
      script.setAttribute('data-kea-support-chat', 'tawk');
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  static loadOnDemand() {
    return this.initialize();
  }

  static validDemoSessionId(value) {
    return /^dms_[a-z0-9_-]{8,156}$/i.test(String(value || ''));
  }

  static setAttributes(attributes) {
    if (typeof window === 'undefined'
      || !window.Tawk_API
      || typeof window.Tawk_API.setAttributes !== 'function') {
      return false;
    }
    try {
      window.Tawk_API.setAttributes(attributes, () => {});
      return true;
    } catch (error) {
      console.error('[Tawk] Error setting support metadata:', error);
      return false;
    }
  }

  static applyPendingIdentity() {
    if (this.pendingDemoSessionId) {
      if (this.setAttributes({ 'kea-demo-session-id': this.pendingDemoSessionId })) {
        this.linkedDemoSessionId = this.pendingDemoSessionId;
      }
      return;
    }
    if (this.linkedDemoSessionId
      && this.setAttributes({ 'kea-demo-session-id': 'consent-withdrawn' })) {
      this.linkedDemoSessionId = null;
    }
  }

  static applyTelemetryPolicy(policy) {
    this.pendingDemoSessionId = policy
      && policy.mode === 'demo_full'
      && this.validDemoSessionId(policy.demoSessionId)
      ? policy.demoSessionId
      : null;
    this.applyPendingIdentity();
  }

  /**
   * Check if Tawk API is available
   */
  static isAvailable() {
    return typeof window !== 'undefined'
      && window.Tawk_API
      && typeof window.Tawk_API.maximize === 'function';
  }

  /**
   * Open/maximize chat window
   */
  static async openChat() {
    const available = this.isAvailable() || await this.initialize();
    if (!available) {
      this.openEmailFallback();
      return false;
    }

    try {
      window.Tawk_API.maximize();
      return true;
    } catch (error) {
      console.error('[Tawk] Error opening chat:', error);
      this.openEmailFallback();
      return false;
    }
  }

  /**
   * Minimize chat window
   */
  static closeChat() {
    if (!this.isAvailable()) {
      return;
    }

    try {
      if (typeof window.Tawk_API.minimize === 'function') {
        window.Tawk_API.minimize();
      }
    } catch (error) {
      console.error('[Tawk] Error closing chat:', error);
    }
  }

  /**
   * Open chat without attaching shop, user, route, or error metadata.
   */
  static async openSupportChat() {
    telemetryClient.capture('support_opened', { screen_name: 'support' });
    return this.openChat();
  }

  /**
   * Fallback to email when Tawk is not available
   * @param {string} subject - Email subject
   */
  static openEmailFallback(subject = 'Support Request') {
    const email = 'support@kealabs.com';
    const encodedSubject = encodeURIComponent(subject);
    window.location.href = `mailto:${email}?subject=${encodedSubject}`;
  }

}

export default TawkHelper;
